/*
  Babblee · product page behaviour
  ===========================================================================
  Gallery, zoom, variants, quantity, add-to-cart, and the live delivery
  estimate. One file, because every one of these has to agree with the others:
  choosing a colour moves the gallery, changes the price, re-enables the button
  and updates the sticky bar, and splitting that across files is how two of them
  end up telling a shopper different things.
*/
(() => {
  const root = document.querySelector('.bb-pdp');
  if (!root) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $ = (sel, scope = root) => scope.querySelector(sel);
  const $$ = (sel, scope = root) => [...scope.querySelectorAll(sel)];

  /* From the document, not from `root`: the variant JSON is a sibling of the
     section rather than a child of it, so scoping this lookup to `.bb-pdp`
     silently returned an empty list — and an empty list makes every option
     "not a combination that exists", so all four colours rendered at 30% and
     could not be clicked. Nothing threw; the page just quietly refused to sell
     anything. */
  let variants = [];
  try { variants = JSON.parse(document.querySelector('[data-bb-variants]')?.textContent || '[]'); } catch { variants = []; }

  /* -------------------------------------------------------------------------
     Gallery
     ---------------------------------------------------------------------- */
  const gallery = $('[data-bb-pdp-gallery]');
  const shots = $$('[data-bb-shot]');
  const thumbs = $$('[data-bb-thumb]');
  const counter = $('[data-bb-counter]');
  let index = 0;

  const showShot = (next) => {
    if (shots.length < 2 && next !== 0) return;
    const to = ((next % shots.length) + shots.length) % shots.length;
    shots[index]?.classList.remove('is-active');
    shots[to]?.classList.add('is-active');
    thumbs.forEach((t, i) => t.classList.toggle('is-active', i === to));
    if (counter) counter.textContent = `${to + 1} / ${shots.length}`;
    index = to;

    /* Keep the active thumbnail in the rail. `nearest` rather than `center`
       so a thumbnail already on screen does not make the rail jump under a
       pointer that is about to click the next one. */
    thumbs[to]?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
  };

  $$('[data-bb-arrow]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      showShot(index + Number(btn.dataset.bbArrow));
    });
  });

  thumbs.forEach((btn, i) => btn.addEventListener('click', (e) => { e.preventDefault(); showShot(i); }));

  /* Swipe on the stage. Distance and direction both required: a vertical drag
     is the page scrolling, and reading a horizontal component out of it would
     change the photograph under a finger that was only trying to scroll. */
  const stage = $('[data-bb-stage]');
  if (stage) {
    let from = null;
    stage.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse') return;
      from = { x: e.clientX, y: e.clientY };
    }, { passive: true });
    stage.addEventListener('pointerup', (e) => {
      if (!from) return;
      const dx = e.clientX - from.x;
      const dy = e.clientY - from.y;
      from = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
      showShot(index + (dx < 0 ? 1 : -1));
      // A swipe ends in a click, and the stage opens the lightbox. Swallow one.
      const swallow = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
      document.addEventListener('click', swallow, { capture: true, once: true });
      setTimeout(() => document.removeEventListener('click', swallow, { capture: true }), 400);
    }, { passive: true });
    stage.addEventListener('pointercancel', () => { from = null; }, { passive: true });
  }

  /* -------------------------------------------------------------------------
     Lightbox
     ---------------------------------------------------------------------- */
  const lb = $('[data-bb-lightbox]');
  const lbImg = $('[data-bb-lb-img]');
  const lbCount = $('[data-bb-lb-count]');
  let lastFocus = null;
  let zoomed = false;
  let pan = { x: 0, y: 0 };

  const sources = shots.map((s) => {
    const img = s.querySelector('img');
    return { src: img?.dataset.bbFull || img?.currentSrc || img?.src || '', alt: img?.alt || '' };
  });

  const paintLb = () => {
    if (!lbImg) return;
    zoomed = false;
    pan = { x: 0, y: 0 };
    lbImg.classList.remove('is-zoomed');
    lbImg.style.transform = '';
    lbImg.src = sources[index]?.src || '';
    lbImg.alt = sources[index]?.alt || '';
    if (lbCount) lbCount.textContent = `${index + 1} / ${sources.length}`;
  };

  const openLb = () => {
    if (!lb) return;
    lastFocus = document.activeElement;
    paintLb();
    lb.classList.add('is-open');
    // The page behind must not scroll under an overlay. This theme scrolls an
    // inner .page-wrapper rather than the window, so locking `body` alone would
    // do nothing at all.
    const scroller = document.querySelector('.page-wrapper') || document.scrollingElement;
    if (scroller) scroller.dataset.bbLocked = scroller.style.overflow || '';
    if (scroller) scroller.style.overflow = 'hidden';
    $('[data-bb-lb-close]')?.focus();
  };

  const closeLb = () => {
    if (!lb) return;
    lb.classList.remove('is-open');
    const scroller = document.querySelector('.page-wrapper') || document.scrollingElement;
    if (scroller) { scroller.style.overflow = scroller.dataset.bbLocked || ''; delete scroller.dataset.bbLocked; }
    if (lastFocus instanceof HTMLElement) lastFocus.focus();
  };

  stage?.addEventListener('click', (e) => {
    if (e.target instanceof Element && e.target.closest('[data-bb-arrow]')) return;
    openLb();
  });
  $('[data-bb-zoom]')?.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); openLb(); });
  $('[data-bb-lb-close]')?.addEventListener('click', closeLb);
  lb?.addEventListener('click', (e) => { if (e.target === lb) closeLb(); });
  $$('[data-bb-lb-nav]').forEach((btn) => btn.addEventListener('click', () => {
    showShot(index + Number(btn.dataset.bbLbNav));
    paintLb();
  }));

  document.addEventListener('keydown', (e) => {
    if (!lb?.classList.contains('is-open')) return;
    if (e.key === 'Escape') { closeLb(); return; }
    if (e.key === 'ArrowRight') { showShot(index + 1); paintLb(); }
    if (e.key === 'ArrowLeft') { showShot(index - 1); paintLb(); }
    if (e.key === 'Tab') {
      // Focus stays inside while the overlay owns the screen.
      const focusables = $$('button', lb).filter((b) => b.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* Click to magnify, drag to move around it. Two states rather than a
     continuous zoom: a shopper checking a seam wants closer, not a slider. */
  lbImg?.addEventListener('click', (e) => {
    e.stopPropagation();
    zoomed = !zoomed;
    lbImg.classList.toggle('is-zoomed', zoomed);
    if (!zoomed) { pan = { x: 0, y: 0 }; lbImg.style.transform = ''; return; }
    const r = lbImg.getBoundingClientRect();
    // Magnify around the point that was clicked, so the detail under the cursor
    // is the detail that gets bigger.
    pan = { x: (r.width / 2 - (e.clientX - r.left)) * 1.1, y: (r.height / 2 - (e.clientY - r.top)) * 1.1 };
    lbImg.style.transform = `scale(2.2) translate(${pan.x / 2.2}px, ${pan.y / 2.2}px)`;
  });

  let dragging = null;
  lbImg?.addEventListener('pointerdown', (e) => {
    if (!zoomed) return;
    dragging = { x: e.clientX, y: e.clientY, ox: pan.x, oy: pan.y };
    lbImg.setPointerCapture(e.pointerId);
  });
  lbImg?.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    pan = { x: dragging.ox + (e.clientX - dragging.x), y: dragging.oy + (e.clientY - dragging.y) };
    lbImg.style.transform = `scale(2.2) translate(${pan.x / 2.2}px, ${pan.y / 2.2}px)`;
  });
  const endDrag = () => { dragging = null; };
  lbImg?.addEventListener('pointerup', endDrag);
  lbImg?.addEventListener('pointercancel', endDrag);

  /* -------------------------------------------------------------------------
     Quantity
     ---------------------------------------------------------------------- */
  const qty = $('#bb-qty');
  const syncQtyButtons = () => {
    const v = Number(qty?.value) || 1;
    const minus = $('[data-bb-qty="-1"]');
    if (minus) minus.disabled = v <= 1;
  };
  $$('[data-bb-qty]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (!qty) return;
      const step = Number(btn.dataset.bbQty) || 0;
      qty.value = String(Math.max(1, (Number(qty.value) || 1) + step));
      qty.classList.remove('is-bumped');
      void qty.offsetWidth; // restart the animation on a repeated press
      qty.classList.add('is-bumped');
      syncQtyButtons();
    });
  });
  qty?.addEventListener('input', syncQtyButtons);
  syncQtyButtons();

  /* -------------------------------------------------------------------------
     Variants
     ---------------------------------------------------------------------- */
  const idField = $('[data-bb-variant-id]');
  const priceEl = $('[data-bb-price-value]');
  const stockEl = $('[data-bb-stock]');
  const addBtn = $('[data-bb-add]');
  const addLabel = $('[data-bb-add-text]');
  const barPrice = $('[data-bb-bar-price]');

  const money = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'EUR',
      }).format(cents / 100);
    } catch { return (cents / 100).toFixed(2); }
  };

  const setPrice = (text) => {
    if (!priceEl || priceEl.textContent === text) return;
    priceEl.textContent = text;
    if (barPrice) barPrice.textContent = text;
    if (reduced) return;
    priceEl.classList.remove('is-changed');
    void priceEl.offsetWidth;
    priceEl.classList.add('is-changed');
  };

  const sync = () => {
    const chosen = [];
    $$('[data-bb-option-index]').forEach((group) => {
      const picked = group.querySelector('input[data-bb-option]:checked');
      chosen.push(picked ? picked.value : null);
      const label = group.querySelector('[data-bb-option-label]');
      if (label && picked) label.textContent = picked.value;
    });

    // Mark values that cannot be reached from the current selection, so a
    // shopper is never left wondering why a chip does nothing.
    $$('[data-bb-option-index]').forEach((group, gi) => {
      group.querySelectorAll('input[data-bb-option]').forEach((input) => {
        const probe = chosen.slice();
        probe[gi] = input.value;
        const matches = (v) => probe.every((val, i) => val === null || v.options[i] === val);
        const exists = variants.some(matches);
        const stocked = variants.some((v) => v.available && matches(v));
        const label = group.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        if (!label) return;
        label.classList.toggle('is-unavailable', exists && !stocked);
        label.classList.toggle('is-missing', !exists);
      });
    });

    if (!variants.length || chosen.some((v) => v === null)) return;
    const match = variants.find((v) => chosen.every((val, i) => v.options[i] === val));

    if (!match) {
      if (addBtn) addBtn.disabled = true;
      if (addLabel) addLabel.textContent = 'Unavailable';
      if (stockEl) stockEl.innerHTML = '<span class="bb-pdp__dot bb-pdp__dot--out"></span> That combination is not available';
      return;
    }

    if (idField) idField.value = match.id;
    setPrice(money(match.price));
    if (addBtn) addBtn.disabled = !match.available;
    if (addLabel) addLabel.textContent = match.available ? 'Add to cart' : 'Sold out';
    if (stockEl) {
      stockEl.innerHTML = match.available
        ? '<span class="bb-pdp__dot"></span> In stock, ready to ship'
        : '<span class="bb-pdp__dot bb-pdp__dot--out"></span> Currently unavailable';
    }

    /* Move the gallery to the chosen colour rather than replacing the stage's
       src. Swapping the src left the thumbnails pointing at a photograph that
       was no longer shown, so the rail and the stage disagreed. */
    if (match.featured_image?.src) {
      const want = String(match.featured_image.src).split('?')[0];
      const found = sources.findIndex((s) => s.src.split('?')[0] === want);
      if (found >= 0 && found !== index) showShot(found);
    }

    document.dispatchEvent(new CustomEvent('bb:variant:change', { detail: { id: match.id }, bubbles: true }));
  };

  $$('input[data-bb-option]').forEach((input) => input.addEventListener('change', sync));
  sync();

  /* -------------------------------------------------------------------------
     Add to cart
     ---------------------------------------------------------------------- */
  const form = $('#bb-pdp-form');
  const added = $('[data-bb-added]');

  form?.addEventListener('submit', async (event) => {
    if (!window.fetch || !addBtn) return;
    event.preventDefault();
    const previous = addLabel?.textContent || 'Add to cart';
    addBtn.classList.add('is-busy');
    addBtn.disabled = true;
    if (addLabel) addLabel.textContent = 'Adding';

    try {
      const response = await fetch('/cart/add.js', { method: 'POST', body: new FormData(form) });
      if (!response.ok) throw new Error('cart');
      addBtn.classList.remove('is-busy');
      addBtn.classList.add('is-done');
      addBtn.disabled = false;
      if (addLabel) addLabel.textContent = 'Added to cart';
      if (added) added.hidden = false;
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      setTimeout(() => {
        addBtn.classList.remove('is-done');
        if (addLabel) addLabel.textContent = previous;
      }, 2400);
    } catch {
      // Never swallow it: fall back to the real form post rather than
      // pretending the item is in the cart.
      addBtn.classList.remove('is-busy');
      addBtn.disabled = false;
      form.submit();
    }
  });

  $('[data-bb-bar-add]')?.addEventListener('click', () => form?.requestSubmit());

  /* The save control's own flourish. The wishlist script owns what a save
     means; this owns only how it feels, so the two never fight over state. */
  const wish = $('[data-bb-wish]');
  wish?.addEventListener('click', () => {
    if (reduced) return;
    wish.classList.remove('is-firing');
    void wish.offsetWidth;
    wish.classList.add('is-firing');
    setTimeout(() => wish.classList.remove('is-firing'), 700);
  });

  /* -------------------------------------------------------------------------
     Sticky purchase bar
     ---------------------------------------------------------------------- */
  const bar = $('[data-bb-bar]');
  const addAnchor = addBtn;
  if (bar && addAnchor && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      ([entry]) => bar.classList.toggle('is-shown', !entry.isIntersecting && entry.boundingClientRect.top < 0),
      { threshold: 0 },
    );
    io.observe(addAnchor);
  }

  /* -------------------------------------------------------------------------
     Live delivery estimate
     ---------------------------------------------------------------------------
     Computed from the reader's own clock, which is the whole point: a printed
     "3-6 working days" is a fact about the warehouse, and the question being
     asked is "will it be here before the weekend". Weekends are skipped on both
     the dispatch day and the transit days, because a parcel does not move on a
     Sunday and a range that pretends otherwise is wrong twice a week.
     ---------------------------------------------------------------------- */
  const delivery = $('[data-bb-delivery]');
  if (delivery) {
    const CUTOFF_HOUR = Number(delivery.dataset.bbCutoff || 15);
    const MIN_DAYS = Number(delivery.dataset.bbMin || 5);
    const MAX_DAYS = Number(delivery.dataset.bbMax || 7);
    const statusEl = $('[data-bb-delivery-status]', delivery);
    const datesEl = $('[data-bb-delivery-dates]', delivery);
    const noteEl = $('[data-bb-delivery-note]', delivery);

    const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;
    const addBusinessDays = (from, n) => {
      const d = new Date(from);
      let left = n;
      while (left > 0) {
        d.setDate(d.getDate() + 1);
        if (!isWeekend(d)) left -= 1;
      }
      return d;
    };
    const fmt = new Intl.DateTimeFormat(document.documentElement.lang || 'en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

    const paint = () => {
      const now = new Date();
      const beforeCutoff = now.getHours() < CUTOFF_HOUR && !isWeekend(now);

      // Dispatch today if we are inside the cutoff on a working day, else the
      // next working day.
      let dispatch = new Date(now);
      if (!beforeCutoff) dispatch = addBusinessDays(now, 1);

      const from = addBusinessDays(dispatch, MIN_DAYS);
      const to = addBusinessDays(dispatch, MAX_DAYS);
      if (datesEl) datesEl.textContent = `Arrives ${fmt.format(from)} – ${fmt.format(to)}`;

      if (beforeCutoff) {
        const cutoff = new Date(now);
        cutoff.setHours(CUTOFF_HOUR, 0, 0, 0);
        const ms = cutoff - now;
        const h = Math.floor(ms / 3600000);
        const m = Math.floor((ms % 3600000) / 60000);
        const s = Math.floor((ms % 60000) / 1000);
        const left = h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m ${String(s).padStart(2, '0')}s`;
        if (statusEl) statusEl.textContent = 'Dispatching today';
        if (noteEl) noteEl.innerHTML = `Order within <span class="bb-delivery__cutoff">${left}</span> to ship today`;
      } else {
        if (statusEl) statusEl.textContent = 'Next dispatch ' + fmt.format(dispatch);
        if (noteEl) noteEl.textContent = `Free delivery over €75 · ${MIN_DAYS}–${MAX_DAYS} business days`;
      }
    };

    paint();
    // A countdown that only ticks while it is on screen. A timer running behind
    // a scrolled-past component is work nobody asked for on a phone.
    let timer = null;
    const run = (on) => {
      if (on && !timer) timer = setInterval(paint, 1000);
      if (!on && timer) { clearInterval(timer); timer = null; }
    };
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => run(e.isIntersecting), { threshold: 0 }).observe(delivery);
    } else {
      run(true);
    }
    document.addEventListener('visibilitychange', () => run(!document.hidden && !!timer));
  }
})();
